import type { CardComparativo, DevedorTop, Insight, Kpis } from '../types.ts';

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

export interface EntradaInsights {
  kpis: Kpis;
  comparativoInadimplenciaMensal: CardComparativo;
  maiorDevedor: DevedorTop | null;
  temVencidoMaisDe90Dias: boolean;
}

// Motor de regras da spec, seção 9. Recalculado a cada mudança de filtro —
// todo texto cita valores do conjunto FILTRADO (vem de `kpis`), nunca números fixos.
export function calcularInsights(entrada: EntradaInsights): Insight[] {
  const { kpis, comparativoInadimplenciaMensal: comp, maiorDevedor, temVencidoMaisDe90Dias } = entrada;
  const insights: Insight[] = [];

  // "em relação a 30 dias atrás", não "ao mês anterior": o comparativo deixou de
  // ser vencido-do-mês × vencido-do-mês-passado e passou a ser o ESTOQUE de
  // dívida em duas datas (ver calcularComparativos). Enquanto o texto dizia mês
  // anterior, ele carimbava de "evolução positiva" a queda artificial que a
  // regra antiga produzia todo mês — foi assim que o painel anunciou -84,2% num
  // período em que a dívida subiu (solicitação 5842).
  if (comp.diferencaPercentual !== null && comp.diferencaPercentual > 5) {
    insights.push({
      tipo: 'alerta',
      mensagem: `🔴 Alerta de inadimplência: a dívida vencida subiu ${comp.diferencaPercentual.toFixed(1)}% (${fmtBRL(comp.diferencaAbsoluta)}) em relação a 30 dias atrás, em ${kpis.vencidoQtdeBoletos} boletos.`,
    });
  } else if (comp.diferencaPercentual !== null && comp.diferencaPercentual < -5) {
    insights.push({
      tipo: 'positivo',
      mensagem: `🟢 Evolução positiva: a dívida vencida caiu ${Math.abs(comp.diferencaPercentual).toFixed(1)}% (${fmtBRL(Math.abs(comp.diferencaAbsoluta))}) em relação a 30 dias atrás.`,
    });
  }

  if (temVencidoMaisDe90Dias) {
    insights.push({
      tipo: 'alerta',
      mensagem: `🔴 Risco concentrado: há boletos vencidos há mais de 90 dias no conjunto filtrado (${kpis.vencidoQtdeBoletos} boletos, ${fmtBRL(kpis.vencido)} no total de vencido).`,
    });
  }

  if (kpis.emAbertoQtdeVencendo7dias > 0) {
    insights.push({
      tipo: 'atencao',
      mensagem: `🟡 Atenção: ${kpis.emAbertoQtdeVencendo7dias} boleto(s) totalizando ${fmtBRL(kpis.emAbertoValorVencendo7dias)} vencem nos próximos 7 dias — janela de lembrete preventivo.`,
    });
  }

  if (maiorDevedor) {
    insights.push({
      tipo: 'atencao',
      mensagem: `🟡 Maior devedor do filtro: ${maiorDevedor.cliente}, ${fmtBRL(maiorDevedor.valorVencido)} em ${maiorDevedor.qtdeBoletos} boleto(s) — tratativa individual recomendada.`,
    });
  }

  if (kpis.recuperado > 0) {
    insights.push({
      tipo: 'positivo',
      mensagem: `🟢 Recuperado: ${fmtBRL(kpis.recuperado)} pagos após o vencimento em ${kpis.recuperadoQtdePagosComAtraso} boleto(s).`,
    });
  }

  if (insights.length === 0) {
    insights.push({ tipo: 'positivo', mensagem: 'Nenhum alerta no momento — carteira sob controle no recorte filtrado.' });
  }

  return insights;
}
